/**
 * assets/component-configurator-3d.js
 * ------------------------------------------------------------------
 * 3D renderer. Registers window.AtioConfigurator.renderers['3d'] so
 * the core controller (component-configurator.js) dispatches here
 * whenever state.view === '3d' — same contract as the 2D renderer.
 *
 * There is no dedicated 3D UI: scaling is driven directly by the
 * width/depth values that already come from the configurator's own
 * sliders (ctx.state.width / ctx.state.depth), expressed as a factor
 * against the model's base size (ctx.standardWidth / ctx.standardDepth
 * — the same A1 reference the 2D view compares against).
 *
 * Per-part scaling
 * ------------------------------------------------------------------
 * <model-viewer>'s public API only exposes materials, not node
 * transforms, so there's no supported way to scale one mesh
 * independently. This reaches past the public API for the internal
 * three.js scene via a property Symbol, then drives that scene
 * directly with three.js — an approach documented (as an explicitly
 * unsupported workaround) by model-viewer's own maintainers:
 * https://modelviewer.dev/docs/faq.html
 *
 * Parts are classified by name (armrest / backrest / seat / penisola)
 * and stretched end-to-end so touching parts stay flush as they grow
 * or shrink — the same scheme the 2D SVG layout uses, just applied to
 * real mesh bounding boxes instead of the composition_layout JSON.
 * Anything that doesn't match one of those names (legs, cushions,
 * etc.) is left at its loaded scale.
 *
 * Mobile parts (follow their Seat, never resized)
 * ------------------------------------------------------------------
 * Any mesh whose name starts with "Mobile" (e.g. a console/tray built
 * into one seat of a modular sofa) sits outside the classification
 * above entirely — it's not stretched by applyWidthFactor/
 * applyDepthFactor the way an armrest/backrest/seat/penisola is, since
 * it's meant to read as a fixed accessory riding on its seat, not as
 * upholstery that grows or shrinks with the composition.
 *
 * Instead, at load time each Mobile is paired with whichever Seat's
 * center is closest to its own (on the WIDTH_AXIS/DEPTH_AXIS ground
 * plane — see pairMobilesWithSeats()), and its offset from that
 * center is recorded as a FRACTION of the Seat's own half-extent on
 * each axis, not a fixed distance. A fixed distance would only track
 * correctly while the Seat purely translates; a Seat with no
 * Backrest/Armrest to chain-stretch from instead purely SCALES about
 * its own pivot (see the "no Backrest"/"no Armrest" fallbacks in
 * applyWidthFactor/applyDepthFactor), which — for a mesh modeled
 * centered on its own origin, as Seats typically are — never moves
 * the Seat's world center at all. A fixed-distance Mobile would then
 * never visibly move either, even as the Seat grows/shrinks under it.
 * The fractional offset instead re-scales by the Seat's CURRENT
 * half-extent every pass (repositionMobiles()), so the Mobile keeps
 * tracking the same relative point on the Seat's surface whichever
 * way the Seat is changing — sliding along for the ride without ever
 * touching its own scale.
 *
 * Separately, Mobiles also step between exactly two sizes based on
 * the overall composition's width/depth: below either
 * MOBILE_SHRINK_WIDTH_THRESHOLD or MOBILE_SHRINK_DEPTH_THRESHOLD
 * (compared against ctx.state.width/ctx.state.depth — the same
 * real-world values the sliders report, not local three.js units)
 * every Mobile is shrunk in X/Z to MOBILE_SHRINK_FACTOR of its loaded
 * size; at or above both thresholds it's exactly its loaded size.
 * This is a plain on/off step re-evaluated every render() pass, not a
 * continuous scale-with-size — see applyMobileShrinkThreshold(),
 * which always runs before repositionMobiles() so the position math
 * above picks up each Mobile's final, already-resolved scale.
 *
 * Shadow tracking
 * ------------------------------------------------------------------
 * model-viewer only recomputes its ground shadow off its own notion
 * of the model's bounding box, which it refreshes on model swaps and
 * on changes made through its public scale/orientation API — not on
 * arbitrary three.js mutations like the per-part stretching above.
 * Left alone, the shadow stays sized to whatever the model looked
 * like on load, so a sofa stretched wider would float above a shadow
 * drawn for the narrower original. updateShadow() below reaches into
 * the same ModelScene instance already exposed via the internal scene
 * Symbol and calls its own updateBoundingBox()/updateShadow() methods
 * after every resize, so the shadow's footprint tracks the model's
 * current bounding box instead of its load-time one.
 *
 * That recompute would also erase the deliberate "floating" gap the
 * ground-lift below creates, since it derives the shadow's height
 * from the same (lifted) mesh positions. updateShadow() briefly
 * subtracts the lift from every mesh, recomputes against that
 * true-ground position, then restores it — so the gap survives
 * resizes too, instead of closing up the first time a slider moves.
 *
 * Model + material selection
 * ------------------------------------------------------------------
 * Which GLB loads, and the starting material, come from the
 * data-model-id / data-material-name attributes on [data-viewer] —
 * set in snippets/configurator-preview-3d.liquid from product
 * metafields (model_3d_id / default_material_name in
 * blocks/product-configurator.liquid).
 *
 * The CURRENT material — after the customer picks a different fabric
 * in the drawer — flows differently: component-configurator.js parses
 * an `atio-material:{name}` tag off each fabric product and stores the
 * resolved value on state.materialName. Every render() call (dimension
 * changes, view switches, fabric picks) reaches this module through
 * the same ctx, so syncMaterial() below just compares
 * ctx.state.materialName against whatever's currently applied and
 * re-fetches only when it actually changed.
 *
 * The material's collection (the folder it's requested from) is never
 * carried alongside the name as a separate value — it's derived
 * straight from the name itself by deriveMaterialCollection() below,
 * which takes the name's leading one or two letters (e.g. "s13" ->
 * collection "s", material "s13"). That keeps the two always in sync
 * by construction instead of relying on whatever produced the name to
 * also produce a matching collection.
 *
 * Asset loading
 * ------------------------------------------------------------------
 * GLB, PBR textures, and the environment map are all fetched on
 * demand through the mint worker (short-lived, signed URLs) and
 * served through the delivery worker — including the environment
 * map, which is shared/non-secret but still goes through the same
 * signed-URL flow rather than a separate unsigned path, so there's
 * one security model to reason about instead of two.
 *
 * ios-src (USDZ, for iOS Quick Look) isn't in the catalog schema yet,
 * so it's intentionally left unset here rather than pointing at a
 * stale path — wire it up the same way as loadModel() once USDZ
 * delivery is added to the catalog.
 */
(function () {
  'use strict';

  window.AtioConfigurator = window.AtioConfigurator || {};
  window.AtioConfigurator.renderers = window.AtioConfigurator.renderers || {};

  var MINT_HOST = 'https://sign.atio.dev';
  var FILL_LIGHT_INTENSITY = 4.8;

  // ── Model axis mapping ───────────────────────────────────────────
  // The configurator's "width" and "depth" don't necessarily line up
  // with the GLB's local X/Z axes — that depends entirely on how the
  // model was exported (e.g. 3ds Max's default export orientation can
  // land a sofa's width on Z and its depth on X instead of the more
  // common X=width/Z=depth convention). Rather than hunting down every
  // 'x'/'z' literal if that ever changes, it's centralized here:
  //   WIDTH_AXIS — the local axis the width slider stretches along
  //   DEPTH_AXIS — the local axis the depth slider stretches along
  // Swap these two values if a future model export uses the more
  // common X=width/Z=depth orientation instead.
  var WIDTH_AXIS = 'x';
  var DEPTH_AXIS = 'z';

  // How far to lift every part off the ground, expressed as a fraction
  // of the model's own height rather than a fixed number of units —
  // that way it stays "slight" regardless of whatever native scale the
  // GLB was exported in (meters, centimeters, ...). Tune by eye.
  var GROUND_LIFT_FRACTION = 0.02;

  // How far touching parts push into each other at their seams,
  // expressed as a fraction of the model's largest horizontal extent
  // (same reasoning as GROUND_LIFT_FRACTION — stays "slight" regardless
  // of native unit scale). 0 reproduces the old hairline-flush look.
  var SEAM_OVERLAP_FRACTION = 0.006;

  const SHEEN_FABRICS = ["e"];

  // ── Mobile shrink threshold ──────────────────────────────────────
  // Below either of these composition dimensions, Mobile parts
  // (consoles/trays — see the "Mobile parts" file header section) are
  // shrunk in X/Z to MOBILE_SHRINK_FACTOR of their loaded size, so
  // they don't visually overwhelm a narrow/shallow composition; once
  // BOTH thresholds are cleared again they snap straight back to
  // their full loaded size. It's a plain two-state step, not a
  // continuous scale-with-size — see applyMobileShrinkThreshold().
  //
  // Units match ctx.state.width / ctx.state.depth (the same
  // real-world values the width/depth sliders already report), NOT
  // the model's local three.js units — these are placeholder values;
  // tune them to the actual catalog range for this composition.
  var MOBILE_SHRINK_WIDTH_THRESHOLD = 180;
  var MOBILE_SHRINK_DEPTH_THRESHOLD = 90;
  var MOBILE_SHRINK_FACTOR = 0.7;

  // ── Penisola row placement ───────────────────────────────────────
  // Which end of the front row (Seats + Penisola, chained together
  // along DEPTH_AXIS in applyDepthFactor) the Penisola belongs at is
  // decided directly from the catalog's data-penisola-position value
  // ("sinistra" | "destra" | "none" — the same value loadModel()
  // already reads to pick which GLB variant to fetch), NOT inferred
  // from mesh geometry. This is deliberate: a "sinistra" and "destra"
  // variant of the same composition can be produced by mirroring the
  // whole assembly at export time, which leaves every individual
  // mesh's own local bounding box identical between the two files —
  // geometry-based side detection then computes the same answer for
  // both variants, which is exactly the bug this replaces.
  //
  // 'start' / 'end' refer to the same two ends splitByReferenceMidpoint
  // already tells apart for Armrests (the armrest nearest fixedDepth
  // vs. the far one) — reusing that vocabulary rather than 'min'/'max'
  // since it's the row's own frame of reference, not a raw coordinate.
  //
  // Whether "sinistra" lands at the 'start' or 'end' of the row was
  // verified against a live composition (sinistra -> 'end', destra ->
  // 'start' for this catalog's export convention). If a future export
  // convention flips it, swap the two values below — that one change
  // corrects every composition at once, no per-model geometry
  // debugging needed.
  var PENISOLA_POSITION_TO_ROW_SIDE = {
    'sinistra': 'end',
    'destra': 'start'
  };

  function derivePenisolaRowSide(penisolaPosition) {
    if (!penisolaPosition) return null;
    return PENISOLA_POSITION_TO_ROW_SIDE[penisolaPosition.toLowerCase()] || null;
  }

  // ── Material naming ──────────────────────────────────────────────
  // Materials are requested from the mint worker at
  // /material/{collection}/{name}, but the catalog only ever gives us
  // the name (e.g. "s13", "fl20") — the collection is just that name's
  // leading letters, so it's derived here rather than carried around
  // as a second value that could drift out of sync with the name.
  // Greedily matches up to 2 leading letters; anything shorter (e.g.
  // a single-letter prefix like "s13") naturally falls back to 1
  // since the regex stops the moment it hits a non-letter.
  function deriveMaterialCollection(materialName) {
    if (!materialName) return null;
    var match = materialName.match(/^[a-zA-Z]{1,2}/);
    return match ? match[0].toLowerCase() : null;
  }

  // ── Asset fetching ──────────────────────────────────────────────
  var assetUrlCache = new Map(); // "model:id" | "material:coll/name" | "env:file" -> {urls, expiresAt}

  function getAssetUrls(cacheKey, fetchPath) {
    var cached = assetUrlCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now() + 15000) return Promise.resolve(cached.urls); // 15s safety margin

    return fetch(fetchPath).then(function (res) {
      if (!res.ok) throw new Error('Failed to load ' + cacheKey);
      return res.json();
    }).then(function (urls) {
      assetUrlCache.set(cacheKey, { urls: urls, expiresAt: Date.now() + 9 * 60 * 1000 }); // just under the 10min TTL
      return urls;
    });
  }

  function loadModel(viewer, modelId, armrestPosition, penisolaPosition) {
    var fullModelName = modelId;

    if (armrestPosition != "none") {
      fullModelName = fullModelName + "_" + armrestPosition;
    }
    else if (penisolaPosition != "none") {
      fullModelName = fullModelName + "_" + penisolaPosition;
    }

    return getAssetUrls('model:' + fullModelName, MINT_HOST + '/model/' + fullModelName).then(function (data) {
      viewer.src = data.glb;
    });
  }

  function loadEnvironment(viewer, filename) {
    return getAssetUrls('env:' + filename, MINT_HOST + '/env/' + filename).then(function (data) {
      viewer.environmentImage = data.url;
      //viewer.environmentImage = '';
    });
  }

  function applyMaterial(viewer, material) {
    material = material.toLowerCase();
    var collection = deriveMaterialCollection(material);
    return getAssetUrls('material:' + collection + '/' + material, MINT_HOST + '/material/' + collection + '/' + material)
      .then(function (urls) {
        var mat = viewer.model && viewer.model.materials.find(function (m) { return m.name === 'Base'; });
        if (!mat) return;

        var pbr = mat.pbrMetallicRoughness;
        var TILING = { u: 1.2, v: 1.2 };
        var NORMAL_SCALE = 1.6;

        return Promise.all([
          viewer.createTexture(urls.diffuse),
          viewer.createTexture(urls.normal),
          viewer.createTexture(urls.orm)
        ]).then(function (textures) {
          var diffuseTex = textures[0], normalTex = textures[1], ormTex = textures[2];

          return Promise.all([
            pbr.baseColorTexture.setTexture(diffuseTex),
            mat.normalTexture.setTexture(normalTex),
            mat.occlusionTexture.setTexture(ormTex),
            pbr.metallicRoughnessTexture.setTexture(ormTex)
          ]).then(function () {
            // apply tiling
            diffuseTex.sampler.setScale(TILING);
            normalTex.sampler.setScale(TILING);
            ormTex.sampler.setScale(TILING);

            // Adjust Normal Scale for enhanced 3D tactile depth
            if (mat.normalTexture.setScale) {
              mat.normalTexture.setScale({ x: NORMAL_SCALE, y: NORMAL_SCALE });
            }

            if (SHEEN_FABRICS.includes(collection)) {
              mat.setSheenColorFactor('#fff');
              mat.setSheenRoughnessFactor(0.05);
            }
          });
        });
      });
  }

  // ── Reaching into model-viewer's internal three.js scene ────────
  function getInternalScene(viewer) {
    var sym = Object.getOwnPropertySymbols(viewer).find(function (s) { return s.description === 'scene'; });
    return sym ? viewer[sym] : null;
  }

  // Walk the three.js graph and collect every node that actually
  // carries geometry (isMesh). Empty organizational nodes (common
  // leftovers from 3ds Max / Blender exports) are transparent: always
  // descend through them rather than stopping just because they have
  // a name — only geometry means "this is a physical part".
  function collectScalableObjects(root) {
    var found = [];
    var anonymousCount = 0;
    function walk(obj) {
      if (obj.isCamera || obj.isLight) return;
      if (obj.isMesh) {
        if (!obj.name || !obj.name.trim().length) {
          anonymousCount += 1;
          obj.name = 'Object ' + anonymousCount;
        }
        found.push(obj);
        return; // meshes are leaves in a glTF node hierarchy
      }
      for (var i = 0; i < obj.children.length; i++) walk(obj.children[i]);
    }
    for (var i = 0; i < root.children.length; i++) walk(root.children[i]);
    return found;
  }

  // Classify a part by name (case-insensitively). Check 'penisola'
  // before 'seat' since the grouping rules differ between them even
  // though both are "seat-like" cushions.
  //
  // 'mobile' is checked first, and with a startsWith (indexOf === 0)
  // test rather than the "appears anywhere" test used for the others
  // — a Mobile follows a Seat rather than being stretched like one, so
  // it needs to be pulled out of the classification before any of the
  // "contains" checks below get a chance to (mis)read it as, say, a
  // Seat because "Mobile_Seat_01" happens to contain "seat" too.
  function getPartType(name) {
    var n = name.toLowerCase();
    if (n.indexOf('mobile') === 0) return 'mobile';
    if (n.indexOf('armrest') > -1) return 'armrest';
    if (n.indexOf('backrest') > -1) return 'backrest';
    if (n.indexOf('penisola') > -1) return 'penisola';
    if (n.indexOf('seat') > -1) return 'seat';
    return null;
  }

  // ── Geometry helpers (bounding-box based stretch/reposition) ────
  // Combined min/max of a group of objects along one axis — used to
  // find the fixed reference edges of a non-scaling group (e.g.
  // Backrest's edges when Backrest itself isn't part of the X factor).
  function combinedBounds(objectBounds, group, axis) {
    return {
      min: Math.min.apply(null, group.map(function (o) { return objectBounds.get(o.uuid).min[axis]; })),
      max: Math.max.apply(null, group.map(function (o) { return objectBounds.get(o.uuid).max[axis]; }))
    };
  }

  // Splits `candidates` into those before vs. after the midpoint of
  // `referenceGroup` along `axis`. Used to tell apart an armrest that
  // caps the START of a chain from one that caps the END of it, when
  // there's more than one armrest.
  function splitByReferenceMidpoint(objectBounds, candidates, referenceGroup, axis) {
    if (referenceGroup.length === 0) return { before: candidates, after: [] };
    var ref = combinedBounds(objectBounds, referenceGroup, axis);
    var refMid = (ref.min + ref.max) / 2;
    var before = [];
    var after = [];
    candidates.forEach(function (obj) {
      var bounds = objectBounds.get(obj.uuid);
      var mid = (bounds.min[axis] + bounds.max[axis]) / 2;
      (mid < refMid ? before : after).push(obj);
    });
    return { before: before, after: after };
  }

  // Rescales AND repositions `obj` along `axis` so its bounding box
  // lands exactly on the world-space span [startCoord, endCoord] —
  // applying "the same factor" to two touching objects only keeps
  // them flush if they're already the same size and start from the
  // same point, which is rarely true. Solving for scale+position from
  // the target span works regardless.
  function stretchToSpan(objectBounds, obj, axis, startCoord, endCoord) {
    var bounds = objectBounds.get(obj.uuid);
    var localMin = bounds.min[axis];
    var localWidth = bounds.max[axis] - bounds.min[axis];
    var factor = (endCoord - startCoord) / localWidth;
    obj.scale[axis] = factor;
    obj.position[axis] = startCoord - localMin * factor;
    return factor;
  }

  function sortByAxis(objectBounds, group, axis) {
    return group.slice().sort(function (a, b) {
      return objectBounds.get(a.uuid).min[axis] - objectBounds.get(b.uuid).min[axis];
    });
  }

  // Stretches a whole sequence of objects end-to-end along `axis`
  // starting at `startCoord`, so a chain of parts stays flush along
  // its whole run, growing or shrinking together. Returns where the
  // chain ends.
  //
  // `seamOverlap` pulls every piece (including the first, relative to
  // `startCoord` itself) back by that amount before stretching it, so
  // touching parts push slightly into one another instead of meeting
  // at a hairline seam. Each piece keeps its own natural size — only
  // where it starts shifts — so the overlap compounds along a chain of
  // several pieces exactly the way physically pushing them together
  // would.
  //
  // `direction` controls which way the chain grows from `startCoord`:
  // +1 (default) grows toward increasing `axis` values, -1 grows
  // toward decreasing ones. Needed for compositions with a single
  // Armrest that lands on the far/high-coordinate side of the row
  // (see applyDepthFactor) — there's nothing on the near side to
  // anchor from, so the chain has to grow backward from the Armrest
  // instead. `sortedGroup` is always passed in ascending order (see
  // sortByAxis) regardless of direction; for direction -1 this walks
  // it back-to-front so the piece nearest `startCoord` is placed
  // first, same as direction 1 does.
  function chainStretch(objectBounds, sortedGroup, axis, startCoord, factor, seamOverlap, direction) {
    direction = direction || 1;
    var ordered = direction === 1 ? sortedGroup : sortedGroup.slice().reverse();
    var edge = startCoord;
    ordered.forEach(function (obj) {
      var bounds = objectBounds.get(obj.uuid);
      var size = (bounds.max[axis] - bounds.min[axis]) * factor;
      var spanStart, spanEnd;
      if (direction === 1) {
        spanStart = edge - seamOverlap;
        spanEnd = spanStart + size;
        edge = spanEnd;
      } else {
        spanEnd = edge + seamOverlap;
        spanStart = spanEnd - size;
        edge = spanStart;
      }
      stretchToSpan(objectBounds, obj, axis, spanStart, spanEnd);
    });
    return edge;
  }

  // How much two objects' ranges overlap along `axis`, as a fraction
  // of the smaller object's own extent. Sequential neighbors (e.g. two
  // Backrests placed one after another) overlap by a few percent at
  // their seam; parallel copies (e.g. two Seats side by side) overlap
  // by nearly 100%. A 50% threshold reliably tells the two apart.
  function overlapFraction(boundsA, boundsB, axis) {
    var overlap = Math.min(boundsA.max[axis], boundsB.max[axis]) - Math.max(boundsA.min[axis], boundsB.min[axis]);
    if (overlap <= 0) return 0;
    var smallerSize = Math.min(boundsA.max[axis] - boundsA.min[axis], boundsB.max[axis] - boundsB.min[axis]);
    return smallerSize > 0 ? overlap / smallerSize : 0;
  }

  // Groups `group` into chains along `axis`: an object joins an
  // existing chain only if it doesn't substantially overlap any
  // object already in it (a genuine sequential neighbor, not a
  // parallel copy). Parallel copies always end up in their own chain,
  // so each stretches independently from the same starting point.
  function clusterForChain(objectBounds, group, axis) {
    var clusters = [];
    group.forEach(function (obj) {
      var bounds = objectBounds.get(obj.uuid);
      var cluster = clusters.find(function (c) {
        return c.every(function (other) {
          var ob = objectBounds.get(other.uuid);
          return overlapFraction(bounds, ob, axis) < 0.5;
        });
      });
      if (cluster) cluster.push(obj);
      else clusters.push([obj]);
    });
    return clusters;
  }

  // Stretches every chain found by clusterForChain as its own
  // independent run, all starting from `startCoord`. Returns the
  // farthest edge reached by any chain.
  function stretchClusteredChain(objectBounds, group, axis, startCoord, factor, seamOverlap, direction) {
    direction = direction || 1;
    var farEdge = startCoord;
    clusterForChain(objectBounds, group, axis).forEach(function (cluster) {
      var edge = chainStretch(objectBounds, sortByAxis(objectBounds, cluster, axis), axis, startCoord, factor, seamOverlap, direction);
      farEdge = direction === 1 ? Math.max(farEdge, edge) : Math.min(farEdge, edge);
    });
    return farEdge;
  }

  // Slides `obj` along `axis`, without changing its scale, so its
  // local min edge lands exactly on `targetCoord`. Used for an
  // armrest that caps the far end of a chain: it never scales, but
  // still needs to follow along as the chain behind it grows/shrinks.
  function repositionToEdge(objectBounds, obj, axis, targetCoord) {
    var bounds = objectBounds.get(obj.uuid);
    obj.position[axis] = targetCoord - bounds.min[axis] * obj.scale[axis];
  }

  // `obj`'s current center along `axis`, in the same parent-space
  // frame as objectBounds. Combines its (rotation-corrected) local
  // bounds with whatever scale+position it currently has — the same
  // two ingredients stretchToSpan() uses to go the other way (target
  // span -> scale+position), so this stays correct whether `obj` has
  // already been resized this pass or is still sitting at its loaded,
  // pre-resize transform.
  function worldCenter(objectBounds, obj, axis) {
    var bounds = objectBounds.get(obj.uuid);
    var localCenter = (bounds.min[axis] + bounds.max[axis]) / 2;
    return obj.position[axis] + localCenter * obj.scale[axis];
  }

  // Slides `obj` along `axis`, without changing its scale, so its
  // current CENTER (not an edge) lands on `targetCenter`. This is
  // repositionToEdge's counterpart for parts that are anchored by
  // their middle rather than by one of their edges — namely Mobile
  // parts (see repositionMobiles below), which need to track a point
  // relative to their paired Seat's center, not either of its ends.
  function repositionCenterTo(objectBounds, obj, axis, targetCenter) {
    var bounds = objectBounds.get(obj.uuid);
    var localCenter = (bounds.min[axis] + bounds.max[axis]) / 2;
    obj.position[axis] = targetCenter - localCenter * obj.scale[axis];
  }

  // Pairs each Mobile with whichever Seat's center is closest to its
  // own on the WIDTH_AXIS/DEPTH_AXIS ground plane, and records the
  // offset between the two centers as a FRACTION of the Seat's own
  // half-extent along each axis — not a fixed world-space distance.
  //
  // A fixed distance only tracks correctly if the Seat purely
  // translates. In practice a Seat can also (or instead) purely SCALE
  // about its own pivot — e.g. a composition with no Backrest/Armrest
  // to chain-stretch from falls back to `obj.scale[axis] = base*factor`
  // with position left untouched (see applyWidthFactor/
  // applyDepthFactor). If the mesh happens to be modeled centered on
  // its own origin (common), that scaling never moves the Seat's
  // world center at all — only its size — so a Mobile anchored by a
  // fixed distance from that center would never visibly move either,
  // even though the Seat is visibly growing/shrinking underneath it.
  // Expressing the offset as "42% of the way from center to the edge"
  // instead of "0.25 units from center" means repositionMobiles() can
  // re-scale it by the Seat's CURRENT half-extent every pass, so the
  // Mobile keeps tracking the same relative point on the Seat's
  // surface whether the Seat is translating, scaling, or both.
  //
  // Called once, in setupInstance(), before any width/depth factor
  // has ever been applied — i.e. against the model's loaded,
  // untouched transforms — so the recorded fraction is exactly "how
  // far off-center this Mobile was authored to sit on its Seat".
  //
  // Nearest-by-distance is a heuristic (nothing in the catalog states
  // which Seat a Mobile belongs to), but it's the same kind of
  // geometry-first inference this file already leans on elsewhere
  // (e.g. clusterForChain), and it degrades gracefully: with one Seat
  // it's unambiguous, and with several it picks whichever one the
  // Mobile was actually modeled next to.
  //
  // A Mobile with no Seat to pair against (composition has none, or
  // seats is empty) is dropped from the result — it's left wherever it
  // loaded and simply never repositioned. A Seat with ~zero extent
  // along an axis (degenerate geometry) can't express a meaningful
  // fraction on that axis either, so the fraction is left at 0 —
  // equivalent to "ride exactly on the Seat's center" rather than
  // dividing by ~zero.
  function pairMobilesWithSeats(objectBounds, mobiles, seats) {
    return mobiles.map(function (mobile) {
      var mobileCenterW = worldCenter(objectBounds, mobile, WIDTH_AXIS);
      var mobileCenterD = worldCenter(objectBounds, mobile, DEPTH_AXIS);

      var nearestSeat = null;
      var nearestDistSq = Infinity;
      seats.forEach(function (seat) {
        var dw = mobileCenterW - worldCenter(objectBounds, seat, WIDTH_AXIS);
        var dd = mobileCenterD - worldCenter(objectBounds, seat, DEPTH_AXIS);
        var distSq = dw * dw + dd * dd;
        if (distSq < nearestDistSq) {
          nearestDistSq = distSq;
          nearestSeat = seat;
        }
      });
      if (!nearestSeat) return null;

      var seatBounds = objectBounds.get(nearestSeat.uuid);
      var seatHalfExtentW = (seatBounds.max[WIDTH_AXIS] - seatBounds.min[WIDTH_AXIS]) / 2;
      var seatHalfExtentD = (seatBounds.max[DEPTH_AXIS] - seatBounds.min[DEPTH_AXIS]) / 2;
      var seatCenterW = worldCenter(objectBounds, nearestSeat, WIDTH_AXIS);
      var seatCenterD = worldCenter(objectBounds, nearestSeat, DEPTH_AXIS);

      var MIN_HALF_EXTENT = 1e-6; // guards the divide below on degenerate geometry
      var offsetFraction = {};
      offsetFraction[WIDTH_AXIS] = seatHalfExtentW > MIN_HALF_EXTENT ? (mobileCenterW - seatCenterW) / seatHalfExtentW : 0;
      offsetFraction[DEPTH_AXIS] = seatHalfExtentD > MIN_HALF_EXTENT ? (mobileCenterD - seatCenterD) / seatHalfExtentD : 0;
      return { mobile: mobile, seat: nearestSeat, offsetFraction: offsetFraction };
    }).filter(Boolean);
  }

  // Sets every Mobile's own X/Z scale to one of exactly two states:
  // MOBILE_SHRINK_FACTOR x its loaded scale once the composition's
  // width OR depth drops below its threshold, or back to precisely
  // its loaded (100%) scale once both thresholds are cleared again.
  // Deliberately NOT interpolated/eased against how far past the
  // threshold the dimension is — the brief was a one-time swap
  // between exactly two sizes, not a smooth scale-with-size — so this
  // is a plain boolean gate, re-evaluated fresh on every render()
  // pass rather than tracked as a one-off event. That's intentional:
  // re-applying the same scale is a no-op, and re-deriving it from
  // scratch each time (rather than remembering "already shrunk")
  // means the widget can't get stuck out of sync if e.g. the width
  // and depth sliders cross their thresholds in opposite directions
  // on the same render pass.
  //
  // Runs against ALL Mobiles, including ones with no paired Seat
  // (see pairMobilesWithSeats) — the threshold is a composition-wide
  // check, not tied to any one Seat, so it applies uniformly.
  //
  // Y is deliberately left alone — only the horizontal footprint
  // shrinks, not the Mobile's height.
  //
  // Must run BEFORE repositionMobiles() in applyDimensionFactors:
  // repositionMobiles() reads each Mobile's CURRENT obj.scale[axis]
  // (via repositionCenterTo) to convert a target center into a
  // position, so the step-scale needs to already be final by the
  // time that conversion happens — otherwise a Mobile that just
  // shrank would be positioned as if it were still full-size.
  function applyMobileShrinkThreshold(data, currentWidth, currentDepth) {
    function getDimensionFactor(dim) {
      if (dim >= 130) return 1.27;
      if (dim >= 115) return 1.1;
      if (dim >= 100) return 0.94;
      if (dim >= 80) return 0.72;
      return 0.72; // Default to smallest scale factor if under 80cm
    }

    var seats = data.seats || [];
    var seatCount = seats.length || 1;
    var totalMobiles = (data.mobiles || []).length;

    // Normalize width checks per seat (e.g., 160cm total / 2 seats = 80cm effective width)
    var effectiveWidth = currentWidth / seatCount;

    // Evaluate factor against BOTH depth and effectiveWidth, picking the smaller factor
    var depthFactor = getDimensionFactor(currentDepth);
    var widthFactor = getDimensionFactor(effectiveWidth);
    var factor = Math.min(depthFactor, widthFactor);

    // Target visibility check using seat-normalized width threshold (< 140cm per seat)
    var targetVisibleCount = (effectiveWidth < 140) ? seatCount : totalMobiles;

    // Enforce floor constraint: visible mobiles can never be less than seatCount
    var finalVisibleCount = Math.max(targetVisibleCount, seatCount);

    var isOneMobilePerSeat = (finalVisibleCount === seatCount);
    var maxAllowedWidthCm = Math.max(0, effectiveWidth - 15);

    (data.mobiles || []).forEach(function (mobile, index) {
      mobile.visible = index < finalVisibleCount;

      var base = data.objectBaseScale.get(mobile.uuid);
      var bounds = data.objectBounds.get(mobile.uuid);

      if (!base || !bounds) return;

      // Identify bounding box extents
      var widthExtent = bounds.max[WIDTH_AXIS] - bounds.min[WIDTH_AXIS];
      var depthExtent = bounds.max[DEPTH_AXIS] - bounds.min[DEPTH_AXIS];

      var longestAxis = widthExtent >= depthExtent ? WIDTH_AXIS : DEPTH_AXIS;

      // Apply the selected dual-check factor to the longest axis only
      var mobileWidthFactor = (longestAxis === WIDTH_AXIS) ? factor : 1.0;
      var mobileDepthFactor = (longestAxis === DEPTH_AXIS) ? factor : 1.0;

      // Cap width when there is 1 mobile per seat so it never exceeds (effectiveWidth - 15cm)
      if (isOneMobilePerSeat && seats.length > 0) {
        var pairedSeat = seats[Math.floor(index % seats.length)] || seats[0];
        var seatBounds = data.objectBounds.get(pairedSeat.uuid);
        var seatBase = data.objectBaseScale.get(pairedSeat.uuid);

        if (seatBounds && seatBase) {
          var seatUnscaledWidth = (seatBounds.max[WIDTH_AXIS] - seatBounds.min[WIDTH_AXIS]) * seatBase[WIDTH_AXIS];
          var mobileUnscaledWidth = widthExtent * base[WIDTH_AXIS];

          if (seatUnscaledWidth > 0) {
            // Calculate mobile base width in real-world cm relative to the seat
            var mobileBaseWidthCm = (mobileUnscaledWidth / seatUnscaledWidth) * effectiveWidth;
            var currentMobileWidthCm = mobileBaseWidthCm * mobileWidthFactor;

            // Clamp width factor if it exceeds effectiveWidth - 15cm
            if (currentMobileWidthCm > maxAllowedWidthCm && mobileBaseWidthCm > 0) {
              mobileWidthFactor = maxAllowedWidthCm / mobileBaseWidthCm;
            }
          }
        }
      }

      // Apply final scale factors
      mobile.scale[WIDTH_AXIS] = base[WIDTH_AXIS] * mobileWidthFactor;
      mobile.scale[DEPTH_AXIS] = base[DEPTH_AXIS] * mobileDepthFactor;
    });
  }

  // Re-applies each Mobile/Seat pairing's recorded offset fraction
  // against its Seat's CURRENT center and CURRENT half-extent. Must
  // run after applyWidthFactor AND applyDepthFactor have both already
  // moved/resized the Seats for this pass — Mobiles are positioned
  // from the Seat's final transform, not scaled themselves, so
  // there's nothing to compute until the Seat has finished changing
  // on both axes.
  function repositionMobiles(data) {
    var objectBounds = data.objectBounds;

    // Detect if any mobile element is currently hidden
    var hasHiddenMobiles = (data.mobiles || []).some(function (mobile) {
      return mobile.visible === false;
    });

    (data.mobilePairings || []).forEach(function (pair) {
      if (!pair.mobile.visible) return;

      // Compute exact current world-space center of the paired seat
      var seatCenterW = worldCenter(objectBounds, pair.seat, WIDTH_AXIS);
      var seatCenterD = worldCenter(objectBounds, pair.seat, DEPTH_AXIS);

      if (hasHiddenMobiles) {
        // Place mobile center directly over seat center on both axes
        repositionCenterTo(objectBounds, pair.mobile, WIDTH_AXIS, seatCenterW);
        repositionCenterTo(objectBounds, pair.mobile, DEPTH_AXIS, seatCenterD);
      } else {
        // Apply authored relative offset fractions when all mobiles are visible
        var seatBounds = objectBounds.get(pair.seat.uuid);
        var seatHalfExtentW = (seatBounds.max[WIDTH_AXIS] - seatBounds.min[WIDTH_AXIS]) / 2 * pair.seat.scale[WIDTH_AXIS];
        var seatHalfExtentD = (seatBounds.max[DEPTH_AXIS] - seatBounds.min[DEPTH_AXIS]) / 2 * pair.seat.scale[DEPTH_AXIS];

        var targetW = seatCenterW + pair.offsetFraction[WIDTH_AXIS] * seatHalfExtentW;
        var targetD = seatCenterD + pair.offsetFraction[DEPTH_AXIS] * seatHalfExtentD;

        repositionCenterTo(objectBounds, pair.mobile, WIDTH_AXIS, targetW);
        repositionCenterTo(objectBounds, pair.mobile, DEPTH_AXIS, targetD);
      }
    });
  }

  // ── Per-instance setup + factor application ─────────────────────
  var instances = new WeakMap(); // <model-viewer> element -> instance data

  function setupInstance(viewer, penisolaPosition) {
    var scene = getInternalScene(viewer);
    if (!scene) {
      console.warn('Atio configurator: could not reach model-viewer\u2019s internal scene — per-part 3D scaling is unavailable in this browser/version.');
      return null;
    }

    var THREE = window.__THREE__;
    var objects = collectScalableObjects(scene);
    var objectBaseScale = new Map();
    var objectBounds = new Map();

    objects.forEach(function (obj) {
      objectBaseScale.set(obj.uuid, { x: obj.scale.x, y: obj.scale.y, z: obj.scale.z });
      obj.geometry.computeBoundingBox();
      var b = obj.geometry.boundingBox;

      // The mesh's own rotation matters here: two objects sharing this
      // same parent can be authored with different rotations (e.g. a
      // Backrest mesh reused 180°-flipped for one side of a
      // composition), and obj.geometry.boundingBox is purely local —
      // it has no idea the mesh is rotated. Left uncorrected, a
      // rotated object's "local max" doesn't point the same physical
      // direction as an unrotated sibling's "local max", so anchoring
      // one against the other (e.g. Seats against a Backrest's
      // combined bounds) silently mixes two different frames. obj's
      // position, unlike its geometry bounds, IS already expressed in
      // the shared parent frame (rotation only affects how a mesh's
      // own vertices sit relative to its own origin, not where that
      // origin sits in the parent), so rotating just the 8 local
      // bounding-box corners — not translating them — produces a box
      // that's directly comparable to every other object's, regardless
      // of which way any individual mesh happens to be turned.
      if (THREE) {
        var corners = [
          new THREE.Vector3(b.min.x, b.min.y, b.min.z),
          new THREE.Vector3(b.min.x, b.min.y, b.max.z),
          new THREE.Vector3(b.min.x, b.max.y, b.min.z),
          new THREE.Vector3(b.min.x, b.max.y, b.max.z),
          new THREE.Vector3(b.max.x, b.min.y, b.min.z),
          new THREE.Vector3(b.max.x, b.min.y, b.max.z),
          new THREE.Vector3(b.max.x, b.max.y, b.min.z),
          new THREE.Vector3(b.max.x, b.max.y, b.max.z)
        ];
        corners.forEach(function (corner) { corner.applyQuaternion(obj.quaternion); });
        var rotatedMin = corners[0].clone();
        var rotatedMax = corners[0].clone();
        corners.forEach(function (corner) {
          rotatedMin.min(corner);
          rotatedMax.max(corner);
        });
        objectBounds.set(obj.uuid, {
          min: { x: rotatedMin.x, y: rotatedMin.y, z: rotatedMin.z },
          max: { x: rotatedMax.x, y: rotatedMax.y, z: rotatedMax.z }
        });
      } else {
        // THREE isn't available (see the fallback below) — fall back
        // to raw local bounds, same as before this fix.
        objectBounds.set(obj.uuid, {
          min: { x: b.min.x, y: b.min.y, z: b.min.z },
          max: { x: b.max.x, y: b.max.y, z: b.max.z }
        });
      }
    });

    // Raise the whole model slightly off the ground. Every part is
    // only ever repositioned along WIDTH_AXIS/DEPTH_AXIS by the resize
    // logic below (Y is never touched), so a one-time uniform lift
    // here survives every later width/depth change without needing to
    // be reapplied. The amount is stashed on `data.groundLift` so
    // updateShadow() (below) can temporarily undo it when recomputing
    // the shadow, keeping the floating gap between model and shadow
    // intact while the shadow's footprint still tracks every resize.
    var seamOverlap = 0;
    var groundLift = 0;
    if (THREE) {
      var modelBox = new THREE.Box3().setFromObject(scene);
      groundLift = (modelBox.max.y - modelBox.min.y) * GROUND_LIFT_FRACTION;
      objects.forEach(function (obj) { obj.position.y += groundLift; });

      var footprint = Math.max(modelBox.max.x - modelBox.min.x, modelBox.max.z - modelBox.min.z);
      seamOverlap = footprint * SEAM_OVERLAP_FRACTION;
    }

    var data = {
      scene: scene,
      objectBaseScale: objectBaseScale,
      objectBounds: objectBounds,
      seamOverlap: seamOverlap,
      shadowWarned: false,
      groundLift: groundLift,
      allObjects: objects,
      penisolaRowSide: derivePenisolaRowSide(penisolaPosition), // 'start' | 'end' | null
      armrests: objects.filter(function (o) { return getPartType(o.name) === 'armrest'; }),
      backrests: objects.filter(function (o) { return getPartType(o.name) === 'backrest'; }),
      seats: objects.filter(function (o) { return getPartType(o.name) === 'seat'; }),
      penisolas: objects.filter(function (o) { return getPartType(o.name) === 'penisola'; }),
      mobiles: objects.filter(function (o) { return getPartType(o.name) === 'mobile'; })
    };

    // Fix each Mobile's offset from its nearest Seat now, while every
    // transform is still exactly as loaded — see pairMobilesWithSeats.
    data.mobilePairings = pairMobilesWithSeats(objectBounds, data.mobiles, data.seats);

    instances.set(viewer, data);
    return data;
  }

  // Shared width, anchored on Backrest (it never scales along the
  // width axis).
  //   - With a Penisola: only the Penisola(s) grow; Armrest + Seat row
  //     stays a fixed width.
  //   - Without one: Seats grow from the Backrest edge, then Armrests
  //     stretch to reach wherever the seats end up.
  function applyWidthFactor(data, factor) {
    var objectBounds = data.objectBounds, armrests = data.armrests, backrests = data.backrests,
        seats = data.seats, penisolas = data.penisolas, seamOverlap = data.seamOverlap || 0;

    if (backrests.length === 0) {
      var targets = penisolas.length ? penisolas : armrests.concat(seats);
      targets.forEach(function (obj) {
        var base = data.objectBaseScale.get(obj.uuid);
        obj.scale[WIDTH_AXIS] = base[WIDTH_AXIS] * factor;
      });
      return;
    }

    var backrestWidth = combinedBounds(objectBounds, backrests, WIDTH_AXIS);

    if (penisolas.length > 0) {
      stretchClusteredChain(objectBounds, penisolas, WIDTH_AXIS, backrestWidth.max, factor, seamOverlap);
      return;
    }

    var seatEdge = stretchClusteredChain(objectBounds, seats, WIDTH_AXIS, backrestWidth.max, factor, seamOverlap);
    armrests.forEach(function (armrest) {
      stretchToSpan(objectBounds, armrest, WIDTH_AXIS, backrestWidth.min - seamOverlap, seatEdge + seamOverlap);
    });
  }

  // Stretches the front row (Seats, plus Penisola if present) from
  // `startCoord` in the given `direction`. When a Penisola is present
  // and its row side is known, its place in the order (before or
  // after the Seats) comes from data.penisolaRowSide — derived from
  // the catalog's data-penisola-position — rather than from
  // clusterForChain's overlap heuristic. That heuristic has to guess
  // whether Seats and Penisola are sequential neighbors or independent
  // parallel groups purely from bounding-box overlap, which is fragile
  // (a Penisola's extent along this axis commonly differs a lot from a
  // Seat's) and, worse, is blind to the fact that "sinistra" vs
  // "destra" variants can share identical local mesh geometry (see the
  // "Penisola row placement" file header comment). Building the row's
  // order explicitly from the known-authoritative side sidesteps both
  // problems: Seats are still ordered among themselves by their own
  // geometry (that part is reliable — it doesn't depend on which side
  // variant loaded), but which end of the row the Penisola sits at is
  // decided, not inferred.
  function stretchFrontRow(data, seats, penisolas, axis, startCoord, factor, seamOverlap, direction) {
    var objectBounds = data.objectBounds;
    if (penisolas.length > 0 && data.penisolaRowSide) {
      var orderedSeats = sortByAxis(objectBounds, seats, axis);
      var orderedPenisolas = sortByAxis(objectBounds, penisolas, axis);
      var orderedFrontRow = data.penisolaRowSide === 'start'
        ? orderedPenisolas.concat(orderedSeats)
        : orderedSeats.concat(orderedPenisolas);
      return chainStretch(objectBounds, orderedFrontRow, axis, startCoord, factor, seamOverlap, direction);
    }
    // No Penisola, or no authoritative side to place it at (e.g. an
    // unrecognized data-penisola-position value) — fall back to the
    // original heuristic-clustered behavior.
    return stretchClusteredChain(objectBounds, seats.concat(penisolas), axis, startCoord, factor, seamOverlap, direction);
  }

  // Shared depth, anchored on Armrest(s) (they never scale along the
  // depth axis). A normal two-arm sofa has an armrest capping both
  // ends — the far one slides along the depth axis to stay flush as
  // the chains grow, without changing size itself.
  // splitByReferenceMidpoint tells the two apart.
  //
  // Which side of the composition's own midpoint a single Armrest
  // lands on is purely a function of that Armrest's actual position in
  // this particular loaded model — nothing guarantees it's always the
  // near/low-coordinate side. A composition with only one Armrest, on
  // the far/high-coordinate side, still needs to anchor to it and grow
  // the interior backward from there; it doesn't mean "no Armrest to
  // anchor to at all" (the old behavior here, before this fix,
  // couldn't tell the two situations apart and fell back to plain
  // per-object scaling with no repositioning for both — which is what
  // let a Seat's own local pivot stay wherever it happened to be
  // authored instead of flush against its neighbor, and is exactly
  // what looked like a Seat overlapping the Armrest and Seats being
  // "switched").
  function applyDepthFactor(data, factor) {
    var objectBounds = data.objectBounds, armrests = data.armrests, backrests = data.backrests,
        seats = data.seats, penisolas = data.penisolas, seamOverlap = data.seamOverlap || 0;

    var frontRow = seats.concat(penisolas);
    var interiorForDepth = backrests.concat(frontRow);
    var split = splitByReferenceMidpoint(objectBounds, armrests, interiorForDepth, DEPTH_AXIS);
    var startArmrests = split.before, endArmrests = split.after;

    if (startArmrests.length === 0 && endArmrests.length === 0) {
      // Truly no Armrest anywhere to anchor against — original
      // fallback, unchanged.
      frontRow.concat(backrests).forEach(function (obj) {
        var base = data.objectBaseScale.get(obj.uuid);
        obj.scale[DEPTH_AXIS] = base[DEPTH_AXIS] * factor;
      });
      return;
    }

    if (startArmrests.length === 0) {
      // No Armrest on the near side, but there is one on the far side
      // — anchor there instead and grow backward (mirror image of the
      // normal case below).
      var fixedDepthFromEnd = combinedBounds(objectBounds, endArmrests, DEPTH_AXIS).min;
      stretchClusteredChain(objectBounds, backrests, DEPTH_AXIS, fixedDepthFromEnd, factor, seamOverlap, -1);
      stretchFrontRow(data, seats, penisolas, DEPTH_AXIS, fixedDepthFromEnd, factor, seamOverlap, -1);
      return;
    }

    var fixedDepth = combinedBounds(objectBounds, startArmrests, DEPTH_AXIS).max;
    var backrestEnd = stretchClusteredChain(objectBounds, backrests, DEPTH_AXIS, fixedDepth, factor, seamOverlap);
    var frontRowEnd = stretchFrontRow(data, seats, penisolas, DEPTH_AXIS, fixedDepth, factor, seamOverlap, 1);

    if (endArmrests.length > 0) {
      var farEdge = Math.max(backrestEnd, frontRowEnd);
      endArmrests.forEach(function (armrest) { repositionToEdge(objectBounds, armrest, DEPTH_AXIS, farEdge - seamOverlap); });
    }
  }

  // Re-derives model-viewer's ground shadow (size + position) from the
  // ModelScene's current bounding box. model-viewer keeps its own
  // boundingBox/size cached and only refreshes them — and the shadow
  // mesh built from them — in response to its own model-swap/scale
  // code paths, so after mutating child transforms directly (as the
  // width/depth factors above do) that cache goes stale until told
  // otherwise. Both methods are internal (not part of <model-viewer>'s
  // documented element API) but, unlike the scene reference itself,
  // aren't hidden behind a Symbol — same "unsupported but reachable"
  // situation as the rest of this file. If a future model-viewer
  // version renames or drops them, this warns once per viewer instance
  // and leaves the shadow at its previous size rather than throwing.
  function updateShadow(data) {
    var scene = data.scene;
    var hasBoundingBoxUpdate = typeof scene.updateBoundingBox === 'function';
    var hasShadowUpdate = typeof scene.updateShadow === 'function';

    if (!hasBoundingBoxUpdate || !hasShadowUpdate) {
      if (!data.shadowWarned) {
        data.shadowWarned = true;
        console.warn('Atio configurator: could not reach model-viewer\u2019s internal shadow update — the ground shadow will stay sized to the model\u2019s original dimensions.');
      }
      return;
    }

    // updateBoundingBox()/updateShadow() derive both the shadow's
    // footprint AND its ground height from wherever the model's
    // meshes currently sit — which includes the one-time
    // GROUND_LIFT_FRACTION lift from setupInstance(). Left as-is, the
    // shadow would rise right along with that lift and close up the
    // floating gap it's meant to create. So the lift is subtracted
    // from every mesh just for this calculation, the shadow is
    // recomputed against that true-ground position (still picking up
    // the model's current, possibly resized, footprint), and then the
    // lift is put straight back before the next render — only the
    // shadow-facing bounding box briefly ignores it, nothing visible
    // ever actually moves.
    var lift = data.groundLift || 0;
    var allObjects = data.allObjects || [];
    if (lift) allObjects.forEach(function (obj) { obj.position.y -= lift; });

    scene.updateBoundingBox();
    scene.updateShadow();

    if (lift) allObjects.forEach(function (obj) { obj.position.y += lift; });
  }

  function applyDimensionFactors(viewer, widthFactor, depthFactor, currentWidth, currentDepth) {
    var data = instances.get(viewer);
    if (!data) return;
    applyWidthFactor(data, depthFactor);
    applyDepthFactor(data, widthFactor);
    // Resolve each Mobile's step-scale before repositioning — see the
    // ordering note on applyMobileShrinkThreshold() itself.
    applyMobileShrinkThreshold(data, currentWidth, currentDepth);
    // Both axes (and now scale) are final for this pass, so each
    // Seat's center is settled and each Mobile's own size is
    // resolved — safe to slide any paired Mobile along with it.
    repositionMobiles(data);
    updateShadow(data);
    data.scene.queueRender();
  }

  // A large overhead "softbox" fill light for the seats. model-viewer's
  // public API only offers environment (IBL) lighting plus a global
  // exposure — no supplemental light source. This adds a real
  // THREE.RectAreaLight directly into the real scene, sized and
  // centered from the model's own bounding box, aimed straight down.
  // RectAreaLight doesn't support shadows, so this only brightens the
  // seats — it doesn't touch the existing ground shadow. Intensity is
  // hardcoded at the value found to look right for this HDRI + tone
  // mapping combination.
  function buildFillLight(viewer) {
    var THREE = window.__THREE__;
    var scene = getInternalScene(viewer);
    if (!THREE || !scene) return;

    var box = new THREE.Box3().setFromObject(scene);
    var size = new THREE.Vector3();
    box.getSize(size);
    var center = new THREE.Vector3();
    box.getCenter(center);

    var fillLight = scene.userData.__atioFillLight;
    if (!fillLight) {
      fillLight = new THREE.RectAreaLight(0xffffff, FILL_LIGHT_INTENSITY, 1, 1);
      fillLight.rotation.x = -Math.PI / 2; // face straight down
      scene.add(fillLight);
      scene.userData.__atioFillLight = fillLight;
    }

    // A bit larger than the model's own footprint, like a real softbox
    // overhanging its subject, positioned above the tallest point
    // (backrests) so it lights the whole seat area evenly.
    fillLight.width = size.x * 1.4;
    fillLight.height = size.z * 1.4;
    fillLight.position.set(center.x, box.max.y + Math.max(size.y, 1) * 1.6, center.z);
    scene.queueRender();
  }

  // ── Wiring into the configurator ─────────────────────────────────
  var materialKeyByViewer = new WeakMap();    // viewer -> "collection/name" currently applied (or in flight)
  var materialTokenByViewer = new WeakMap();  // viewer -> request counter, to ignore stale async resolutions

  // Re-applies the material only when it actually changed, and only
  // acts on the latest request if the fabric is switched again before
  // the previous applyMaterial() call has finished (e.g. clicking
  // through several swatches quickly) — the older, now-stale request
  // is left to resolve without touching the model. The collection
  // needed for the request is derived from `material` itself via
  // deriveMaterialCollection() — nothing upstream needs to supply it
  // separately.
  function syncMaterial(viewer, material) {
    console.log(material);
    if (!material) return;

    material = material.toLowerCase();
    var collection = deriveMaterialCollection(material);
    var key = collection + '/' + material;
    if (materialKeyByViewer.get(viewer) === key) return;
    materialKeyByViewer.set(viewer, key);

    var token = (materialTokenByViewer.get(viewer) || 0) + 1;
    materialTokenByViewer.set(viewer, token);

    applyMaterial(viewer, material).catch(function (err) {
      console.error('Atio configurator: material update failed', err);
    }).then(function () {
      if (materialTokenByViewer.get(viewer) !== token) return; // a newer switch has since started
      viewer.dispatchEvent(new CustomEvent('atio-material-applied', { detail: { collection: collection, material: material } }));
    });
  }

  function initViewer(mount, ctx) {
    var viewer = mount.querySelector('[data-viewer]');
    var statusBar = mount.querySelector('[data-view-3d-status]');
    var arButton = mount.querySelector('[data-view-3d-ar]');
    if (!viewer) return;

    var modelId = viewer.dataset.modelId || 'a1';
    var materialName = viewer.dataset.materialName || 'flow_20';
    var armrestPosition = viewer.dataset.armrestPosition || 'none';
    var penisolaPosition = viewer.dataset.penisolaPosition || 'none';

    function setStatus(msg, isErr) {
      if (!statusBar) return;
      statusBar.hidden = !msg;
      statusBar.textContent = msg || '';
      statusBar.classList.toggle('is-error', !!isErr);
    }

    viewer.addEventListener('load', function () {
      setStatus('');
      if (arButton) arButton.hidden = !viewer.canActivateAR;

      applyMaterial(viewer, materialName).catch(function () {}).then(function () {
        var normalizedMaterialName = materialName.toLowerCase();
        materialKeyByViewer.set(viewer, deriveMaterialCollection(normalizedMaterialName) + '/' + normalizedMaterialName);

        setupInstance(viewer, penisolaPosition);
        buildFillLight(viewer);
        viewer.__atioReady = true;

        // Apply whatever width/depth/material the configurator is
        // already at, in case the sliders or fabric drawer were used
        // before the model finished loading.
        var widthFactor = ctx.state.width / (ctx.standardWidth || ctx.state.width);
        var depthFactor = ctx.state.depth / (ctx.standardDepth || ctx.state.depth);
        applyDimensionFactors(viewer, widthFactor, depthFactor, ctx.state.width, ctx.state.depth);
        syncMaterial(viewer, ctx.state.materialName);
      });
    });

    viewer.addEventListener('error', function () {
      setStatus('Impossibile caricare il modello 3D', true);
    });

    setStatus('Caricamento\u2026');
    loadEnvironment(viewer, 'studio_small_09_2k.jpg').catch(function (err) { console.error('Environment load failed', err); });
    loadModel(viewer, modelId, armrestPosition, penisolaPosition).catch(function (err) { setStatus('Impossibile caricare il modello 3D', true); });
  }

  function render(ctx) {
    var mount = ctx.view3dRoot;
    if (!mount) return;

    if (!mount.dataset.atioInit) {
      mount.dataset.atioInit = 'true';
      initViewer(mount, ctx);
      return; // wait for the 'load' event before the first scale pass
    }

    var viewer = mount.querySelector('[data-viewer]');
    if (!viewer || !viewer.__atioReady) return; // model still loading

    var widthFactor = ctx.state.width / (ctx.standardWidth || ctx.state.width);
    var depthFactor = ctx.state.depth / (ctx.standardDepth || ctx.state.depth);
    applyDimensionFactors(viewer, widthFactor, depthFactor, ctx.state.width, ctx.state.depth);
    syncMaterial(viewer, ctx.state.materialName);
  }

  window.AtioConfigurator.renderers['3d'] = {
    render: render
  };
})();